import { agencyCalendarDate, formatRecordedCalendarDate } from '@vigeo/shared';
import type { AuditAction, CommunicationStatus, StepState, TimelineEntry } from '@vigeo/shared';

/**
 * The human sentence for every audit action (#62).
 *
 * `as const satisfies Record<AuditAction, …>`: adding an audit action is a compile error here
 * until somebody writes what it means to a reader, rather than a blank row appearing in
 * production. tests/timeline-presenters.test.ts iterates the enum at runtime too.
 */
export interface TimelinePresenter {
  readonly title: string;
}

export const auditPresenters = {
  'person.created': { title: 'Record created' },
  'person.updated': { title: 'Record updated' },
  'person.history-recorded': { title: 'History recorded' },
  'person.addendum-recorded': { title: 'History addendum recorded' },
  'person.lifecycle-changed': { title: 'Stage changed' },
  'person.sensitive-updated': { title: 'Protected identity details updated' },
  'person.sensitive-revealed': { title: 'Protected identity detail revealed' },
  // Reads as the refusal it is, beside the reservation row that precedes it (#371).
  'person.sensitive-reveal-refused': { title: 'Sensitive field reveal refused' },
  // Written only for a segregated form — health or I-9 (ADR-013 §4). Reading an ordinary form's
  // answers is not an event, so a row with this title always means a restricted one.
  'step.answers-viewed': { title: 'Protected answers viewed' },
  /*
    The other half of that row (#385): somebody asked for a segregated form's answers and was
    refused. Written so repeated attempts are visible rather than only the successful ones.

    The title says REFUSED rather than naming what was protected, and the row carries no `answers`
    block, because a refusal must not confirm to a trail reader that a segregated form exists —
    `file.access-refused`'s rule (#184).
  */
  'step.answers-refused': { title: 'Protected answers refused' },
  'requirement.exception-granted': { title: 'Policy waiver granted' },
  'requirement.exception-revoked': { title: 'Policy waiver revoked' },
  'step.issued': { title: 'Item requested' },
  'step.sent': { title: 'Item sent to the person' },
  'step.started': { title: 'Person started an item' },
  'step.draft-saved': { title: 'Person worked on an item' },
  'step.submitted': { title: 'Person submitted an item' },
  // In addition to `step.submitted`, never instead of it (ADR-014 §3, #215). The row carries only
  // the evidence record's id; the IP address and user agent are on that record, never here.
  'step.signed': { title: 'Person signed an item' },
  'step.approved': { title: 'Item approved' },
  'step.rejected': { title: 'Item sent back' },
  'step.cancelled': { title: 'Item cancelled' },
  'definition.published': { title: 'Form published' },
  'definition.versioned': { title: 'Form updated' },
  'definition.retired': { title: 'Form retired' },
  'external-checkpoint.set': { title: 'Outside check updated' },
  'effect.resumed': { title: 'Stalled work handed back for another try' },
  'token.issued': { title: 'Portal link created' },
  'token.used': { title: 'Portal link opened' },
  'token.failed': { title: 'Portal sign-in failed' },
  'token.locked': { title: 'Portal link locked after failed attempts' },
  'token.revoked': { title: 'Portal link revoked' },
  'token.re-requested': { title: 'New portal link requested' },
  'upload.received': { title: 'File received' },
  'upload.rejected': { title: 'File refused' },
  'upload.scanned': { title: 'File scanned for malware' },
  'file.routed': { title: 'File filed' },
  'file.route-parked': { title: 'Filing waiting' },
  'file.route-blocked': { title: 'Filing blocked' },
  'file.stored': { title: 'File saved to the record' },
  'file.store-parked': { title: 'Saving a file is waiting' },
  'file.store-blocked': { title: 'Saving a file was blocked' },
  'file.viewed': { title: 'File viewed' },
  'file.downloaded': { title: 'File downloaded' },
  // Reads as the correction it is: the row above it says viewed, and this says nothing arrived.
  'file.serve-failed': { title: 'File could not be served' },
  'file.access-refused': { title: 'File access refused' },
  'file.list-refused': { title: 'File list access refused' },
  'file.correction-recorded': { title: 'Misfiled file correction recorded' },
  'file.misfiled-version-removed': { title: 'Misfiled copy removed' },
  'email.queued': { title: 'Email queued' },
  'email.sent': { title: 'Email sent' },
  'email.failed': { title: 'Email failed' },
  'email.parked': { title: 'Email waiting for review' },
  'credential.minted': { title: 'Credential recorded' },
  'verification.recorded': { title: 'Verification recorded' },
  'adhoc.request-created': { title: 'One-off request created' },
  'upload.marked-not-ready': { title: 'Upload marked not ready' },
  'upload.link-requested': { title: 'Upload link requested' },
  'packet.snapshotted': { title: 'Packet snapshot saved' },
  'pdf-template.published': { title: 'PDF template published' },
  'pdf-template.integrity-failed': { title: 'PDF template failed its integrity check' },
} as const satisfies Record<AuditAction, TimelinePresenter>;

export const communicationStatusPresenters = {
  queued: { label: 'Queued' },
  sending: { label: 'Sending' },
  sent: { label: 'Sent' },
  failed: { label: 'Failed' },
  parked: { label: 'Not sent — needs review' },
} as const satisfies Record<CommunicationStatus, { readonly label: string }>;

export const stepStatePresenters = {
  not_started: { label: 'Not started' },
  sent: { label: 'Sent' },
  in_progress: { label: 'In progress' },
  submitted: { label: 'Submitted' },
  approved: { label: 'Approved' },
  rejected: { label: 'Sent back' },
  cancelled: { label: 'Cancelled' },
} as const satisfies Record<StepState, { readonly label: string }>;

export const actorLabels = { 'hr-user': 'HR', 'portal-visitor': 'The person', system: 'System' } as const;

/** The row's heading: the audit sentence, or what the row is when no event names it. */
export function entryTitle(entry: TimelineEntry): string {
  switch (entry.kind) {
    case 'event':
      return auditPresenters[entry.action].title;
    case 'communication':
      return 'Email';
    case 'step':
      return entry.step.adHoc ? 'One-off request' : 'Item requested';
    case 'interview':
      return 'Interview';
  }
}

/**
 * The interview row's second line: who was in it and where the candidate came from (#278).
 * Names only — an interviewer's directory id means nothing to HR. Empty string when neither is
 * known, which the row treats as nothing to add.
 */
export function interviewDetail(entry: Extract<TimelineEntry, { kind: 'interview' }>): string {
  const who = entry.interviewers.length === 0 ? null : `Interviewed by ${entry.interviewers.map((person) => person.name).join(', ')}`;
  const where = entry.foundUsThrough === null ? null : `Found us through ${entry.foundUsThrough}`;
  return [who, where].filter((part) => part !== null).join(' · ');
}

/**
 * WHEN a row happened, as its own kind of fact deserves to be read.
 *
 * Most entries are real instants — an email queued, a step approved — and a reader wants those in
 * their own timezone, to the minute. An INTERVIEW is not one of those. HR typed a calendar date,
 * and `buildTimelinePage` positions it at the start of that day in the agency's zone so it sorts
 * into the chronology. Printing that instant with `toLocaleString()` did two wrong things at once:
 * it invented a clock time HR never gave ("9:00:00 PM"), and for any viewer west of the agency it
 * printed the PREVIOUS day — so the History row and the "How they came to us" card on the same
 * person disagreed about the date of the same interview.
 *
 * So an interview reads as the calendar date it was, in the agency's zone, with no time at all —
 * which is exactly what the profile card shows, from the stored date. A test pins the two together.
 *
 * ITS LOCALE IS DELIBERATELY THE AGENCY'S, not the viewer's, and that is a real divergence from the
 * rows around it: the other branch calls `toLocaleString(undefined)`, which follows the browser,
 * while this one lets `formatRecordedCalendarDate` apply its `en-US` default. Agreeing with the
 * profile card on the same person matters more than agreeing with a sibling row's locale — a date
 * that reads differently in two places on one screen is the bug this function was written to fix.
 * Said out loud because silence about it would look like an oversight.
 */
export function entryWhen(entry: TimelineEntry, locale?: string): string {
  if (entry.kind === 'interview') return formatRecordedCalendarDate(agencyCalendarDate(entry.at), locale);
  return new Date(entry.at).toLocaleString(locale);
}
